import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/shared/lib/cn';

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full font-bold whitespace-nowrap no-underline transition-colors duration-150 ease-standard disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        outline: 'border border-primary bg-transparent text-primary hover:bg-accent',
        ghost: 'bg-transparent text-accent-foreground hover:bg-accent',
        danger: 'bg-destructive text-destructive-foreground hover:bg-destructive-hover',
        accent: 'bg-highlight text-highlight-foreground hover:bg-highlight-hover',
      },
      size: {
        sm: 'h-8 px-4 text-sm',
        default: 'h-10 px-6 text-base',
        lg: 'h-12 px-8 text-base',
        'icon-sm': 'size-8',
        icon: 'size-10',
        'icon-lg': "size-12 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child (a link, usually) with button styling instead of a `<button>`. */
    asChild?: boolean;
  };

/**
 * The only button. Pills; six variants (accent = yellow, at most one per screen); `sm` is the
 * compact-density size; `icon*` sizes are round and need an `aria-label`.
 */
function Button({
  className,
  variant = 'primary',
  size = 'default',
  asChild = false,
  type,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...(asChild ? {} : { type: type ?? 'button' })}
      {...props}
    />
  );
}

export { Button, type ButtonProps, buttonVariants };
